import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, Table, Alert, message, Button } from "antd";
import { apis, AUTH_HEADER_NAME } from "common/apis";
import { IPurchaseIndentRow, IPurchaseIndentUpload } from "common/interface";
import { IAppError } from "common/interface/IApiError";
import { routes } from "common/routes";
import { AppPageHeader } from "components/AppPageHeader";
import { AppUpload } from "components/AppUpload";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import {
  removeProcessingVendorAssignment,
  setProcessingVendorAssignment,
} from "store/purchase-indent/purchaseIndentSlice";
import { generateHeaders } from "common/utils";
import { ColumnsType } from "antd/lib/table";
import { useDocumentTitle } from "hooks/usePageTitle";
import { getWarehouseId } from "services/PersistService/PersistWareHouseReturn";
import { getWhDivisionId } from "services/PersistService/PersistDivisionService";

const VendorAssignmentUploadPage = () => {
  useDocumentTitle("Upload Vendor Assignment");
  const { authToken } = useAppSelector((state) => state.app.user);
  const { processingVendorAssignment } = useAppSelector(
    (state) => state.purchaseIndent
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [hasErrorRows, setHasErrorRows] = useState(false);

  useEffect(() => {
    const { timeUploaded } = processingVendorAssignment;
    if (timeUploaded) {
      const uploadedTime = new Date(timeUploaded).getTime();
      const currentTime = new Date().getTime();
      const minsPassed = (currentTime - uploadedTime) / 1000 / 60;

      if (minsPassed >= 15) {
        dispatch(removeProcessingVendorAssignment());
      }
    }

    if (processingVendorAssignment && processingVendorAssignment.key) {
      navigate(`/${routes.PURCHASE_INDENT.SAVE_VENDOR_ASSIGNMENT}`, {});
    }

    return () => {
      message.destroy();
    };
  }, [processingVendorAssignment, navigate, dispatch]);

  useEffect(() => {
    const failed = processingVendorAssignment?.failedRows?.length;
    if (failed) {
      setHasErrorRows(true);
    }
  }, [processingVendorAssignment?.failedRows]);

  const headers: { [name: string]: string } = {
    ...(authToken && { [AUTH_HEADER_NAME]: authToken }),
  };

  const onUploaded = (data: IPurchaseIndentUpload) => {
    dispatch(setProcessingVendorAssignment(data));
  };

  const onUploading = () => {
    dispatch(removeProcessingVendorAssignment());
  };

  const onDownloadIndents = () => {
    const whId = getWarehouseId();
    const whDivId = getWhDivisionId();

    const fetchHeaders: Record<string, string> = {
      "Content-Type": "application/json",
      appId: "wms-portal",
    };
    if (authToken) fetchHeaders[AUTH_HEADER_NAME] = authToken;
    if (whId) fetchHeaders["wh-id"] = String(whId);
    if (whDivId) fetchHeaders["wh-div-id"] = String(whDivId);

    fetch(apis.vendorAssignmentTemplate, { headers: fetchHeaders })
      .then((res) => {
        if (!res.ok) throw new Error("Download failed");
        return res.blob();
      })
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = "vendor_assignment_indents.csv";
        a.click();
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch(() => message.error("Failed to download indents"));
  };

  const { headerMapping } = processingVendorAssignment;
  const headerMappingWithError = headerMapping + ",errors:Errors";

  const generateMemoizedHeaders = useMemo(
    () => generateHeaders(headerMappingWithError),
    [headerMappingWithError]
  );

  const tableHeaders = generateMemoizedHeaders || {};
  const successRows: IPurchaseIndentRow[] = processingVendorAssignment?.successRows || [];
  const failedRows: IPurchaseIndentRow[] = processingVendorAssignment?.failedRows || [];
  const successData = successRows.map((row: IPurchaseIndentRow, i: number) => ({
    ...row,
    key: `data-success-${i}`,
  }));
  const failedData = failedRows.map((row: IPurchaseIndentRow, i: number) => ({
    ...row,
    key: `data-failed-${i}`,
  }));
  const data = [...failedData, ...successData];

  const columns: ColumnsType<IPurchaseIndentRow & { key: string }> = [
    ...Object.keys(tableHeaders).map((key) => ({
      key,
      title: tableHeaders[key],
      dataIndex: key,
      render: (text: string, record: IPurchaseIndentRow & { key: string }) => {
        if (key === "errors") {
          const errorMessages = (record.errors as IAppError[])
            ?.map((error: IAppError) => error.message)
            .join(", ");
          return <>{errorMessages}</>;
        }
        return <>{text}</>;
      },
    })),
  ];

  const addBorderToErrorRows = (
    record: IPurchaseIndentRow & { key: string }
  ) => {
    if (record.errors && record.errors.length) {
      return "error-row";
    }
    return "";
  };

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Upload Vendor Assignment" />
        </Col>
      </Row>
      <Row style={{ marginBottom: 16 }}>
        <Button type="primary" onClick={onDownloadIndents}>
          Download Indents
        </Button>
      </Row>

      <Row>
        <Col lg={16} xs={24}>
          <AppUpload
            uploadUrl={apis.uploadVendorAssignment}
            headers={headers}
            onUploaded={onUploaded}
            onUploading={onUploading}
            hasErrorRows={hasErrorRows}
          />
        </Col>
      </Row>

      {!!Object.keys(processingVendorAssignment).length &&
        !processingVendorAssignment.key && (
          <>
            <Row>
              <Col lg={16} xs={24}>
                <Alert
                  type="error"
                  message="Errors in sheet data."
                  description="Please correct errors and upload again."
                  showIcon
                  banner
                />
              </Col>
            </Row>

            <Row>
              <Table
                size="small"
                columns={columns}
                dataSource={data}
                rowClassName={(record) => addBorderToErrorRows(record)}
                scroll={{ y: 500, x: "100vh" }}
                className="table-with-errors"
              />
            </Row>
          </>
        )}
    </>
  );
};

export default VendorAssignmentUploadPage;
